import { useState } from 'react';

import { toastService } from '@thefarmersfront/kitchen';

import { registerMockAPI } from '~/mocks/mockApis';

import { useAPI, useAsyncHandler } from '~/hooks/common';
import { addIf } from '~/utils';

import { API_ENDPOINT, STOCK_KEYWORD_TYPE_FILTER_KEY, TOAST_MESSAGE } from '~/constants';

import { TypeErrorResponseData } from '~/types/common.types';
import {
  TypeGetVendorReleaseInventoryParams,
  TypeGetVendorReleaseInventoryResponse,
  TypeStockReleaseGoods,
  TypeStockReleaseInventoryFilter,
} from '~/types/stock.types';

const useGetVendorReleaseInventory = () => {
  const { runAsync } = useAsyncHandler();

  const [responseData, setResponseData] = useState<TypeGetVendorReleaseInventoryResponse | null>(null);

  const postVendorReleaseInventory = useAPI<TypeGetVendorReleaseInventoryParams, TypeGetVendorReleaseInventoryResponse>(
    {
      endpoint: API_ENDPOINT.STOCK.POST_VENDOR_RELEASE_INVENTORY,
      method: 'post',
    }
  );

  const requestVendorReleaseInventory = (
    filterState: TypeStockReleaseInventoryFilter,
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (data: TypeGetVendorReleaseInventoryResponse) => void;
      onError?: (error: TypeErrorResponseData['error']) => void;
    } = {}
  ) => {
    const { cluster, keywordType, keywordValue, pageSize, currentPage } = filterState;
    const keywordFilterKey =
      STOCK_KEYWORD_TYPE_FILTER_KEY[keywordType?.value as keyof typeof STOCK_KEYWORD_TYPE_FILTER_KEY];

    if (!cluster?.value) {
      return;
    }

    const params: TypeGetVendorReleaseInventoryParams = {
      cluster: cluster.value,
      ...addIf(keywordFilterKey && keywordValue, keywordFilterKey, keywordValue),
      cursor: currentPage,
      limit: pageSize,
    };

    runAsync(() => postVendorReleaseInventory({ params }), {
      onSuccess: (res) => {
        setResponseData(res);
        onSuccess?.(res);
      },
      onError: (error: TypeErrorResponseData['error']) => {
        toastService.addToast({
          type: 'error',
          message: error.message ?? TOAST_MESSAGE.DEFAULT_ERROR,
        });
        onError?.(error);
      },
    });
  };

  const resetResponseData = () => {
    setResponseData(null);
  };

  return { requestVendorReleaseInventory, responseData, resetResponseData };
};

export default useGetVendorReleaseInventory;

const mockReleaseGoods = (index: number, overrides: Partial<TypeStockReleaseGoods> = {}): TypeStockReleaseGoods => ({
  skuCode: `M000005076${String(index).padStart(2, '0')}`,
  barcode: `88123123150${index}`,
  clusterCode: 'CC02',
  clusterCodeObject: { code: 'CC02', name: '김포' },
  skuName: `(센터명)반출상품${index}`,
  temperatureType: 'COLD',
  temperatureTypeCodeObject: { code: 'COLD', name: '냉장' },
  totalQuantity: 500,
  availableQuantity: 400,
  availableRequestQuantity: 0,
  allocationQuantity: 50,
  holdingQuantity: 20,
  defectiveQuantity: 30,
  defectiveRequestQuantity: 0,
  ...overrides,
});

const vendorReleaseInventoryNormalResponse: TypeGetVendorReleaseInventoryResponse = {
  data: [
    mockReleaseGoods(1),
    mockReleaseGoods(2, {
      temperatureType: 'ROOM',
      temperatureTypeCodeObject: { code: 'ROOM', name: '상온' },
      availableQuantity: 12000,
      totalQuantity: 12100,
    }),
    mockReleaseGoods(3, { defectiveQuantity: 0 }),
    mockReleaseGoods(4, { skuName: '(센터명)이름이 아주 긴 반출 상품명 — 말줄임 확인용 긴 상품명 텍스트' }),
  ],
  pagination: { listView: 100, listTotal: 4, cursor: 0 },
};

registerMockAPI<TypeGetVendorReleaseInventoryResponse>({
  method: 'post',
  endpoint: API_ENDPOINT.STOCK.POST_VENDOR_RELEASE_INVENTORY,
  label: '재고/반출요청등록/반출가능재고',
  scenarios: [
    { name: '정상 응답', response: vendorReleaseInventoryNormalResponse },
    { name: '검색 결과 0건', response: { data: [], pagination: { listView: 100, listTotal: 0, cursor: 0 } } },
  ],
});
